import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can, isCommittedStage, isCentralRole, canDeleteRecord } from "@/lib/rbac";
import { useConfig } from "@/lib/useConfig";
import { PageHead } from "@/components/Bits";
import { CreateLeadDialog } from "@/components/CreateLeadDialog";
import { BackdateField } from "@/components/BackdateField";
import { JobsBoard } from "@/components/JobsBoard";
import { ExportButtons } from "@/components/ExportButtons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EXPORT_COLUMNS = [
  { label: "Studio", value: (j) => j.studio_name || "" },
  { label: "Customer", value: (j) => j.customer_name },
  { label: "Vehicle", value: (j) => (j.vehicle ? `${j.vehicle.make} ${j.vehicle.model}` : "") },
  { label: "PPF Installer", value: (j) => j.installer_name || "Unassigned" },
  { label: "Status", value: (j) => j.status },
  { label: "Services", value: (j) => (j.services || []).join("; ") },
  { label: "Created", value: (j) => fmtDate(j.created_at) },
  { label: "Completed", value: (j) => fmtDate(j.completed_at) },
  { label: "Revenue", value: (j) => j.price },
  { label: "Material cost", value: (j) => j.material_cost || 0 },
  { label: "Profit", value: (j) => j.price - (j.material_cost || 0) },
];

export default function Jobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const serviceOptions = useConfig("services");
  const stageConfig = useConfig("pipeline-stages");
  // Always use the network's configured pipeline — never the generic built-in fallback.
  const baseStages = stageConfig.map((s) => s.name);
  const STATUSES = baseStages.length && !baseStages.includes("Not Interested")
    ? [...baseStages, "Not Interested"]
    : baseStages;
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [installerSource, setInstallerSource] = useState("hq");
  const [csUsers, setCsUsers] = useState([]);
  const [studios, setStudios] = useState([]);
  const [form, setForm] = useState({
    customer_id: "",
    installer_id: "",
    services: "",
    status: "Lead",
    price: "",
    cs_person_id: "",
    studio_id: "",
    ppf_sqft: "",
  });
  const [priceInfo, setPriceInfo] = useState(null);
  const canWrite = can(user, "jobs:write");
  const canDelete = can(user, "jobs:delete");
  const isCentral = can(user, "jobs:write") && !user.studio_id;
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");

  const q = search.trim().toLowerCase();
  const filteredJobs = q
    ? jobs.filter((j) => {
        const veh = j.vehicle ? `${j.vehicle.make || ""} ${j.vehicle.model || ""}` : "";
        return [j.customer_name, veh, j.customer_phone, j.id, j.human_id]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
    : jobs;

  const doDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/jobs/${delTarget.id}`);
      toast.success(`${delTarget.customer_name}'s job deleted`);
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const moveJob = async (job, newStatus) => {
    if (newStatus === "Not Interested") {
      toast.info("Open the job to mark it Not Interested and record a reason");
      return;
    }
    if (newStatus === "Booked") {
      toast.info("Open the job and set it to Booked to enter the booking date");
      return;
    }
    setJobs((js) => js.map((j) => (j.id === job.id ? { ...j, status: newStatus } : j)));
    try {
      await api.put(`/jobs/${job.id}`, { status: newStatus });
      toast.success(`${job.customer_name} moved to ${newStatus}`);
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (/Complete these lead details/i.test(detail)) {
        toast.error("Open this lead to complete the car & source details before moving to " + newStatus);
      } else {
        toast.error(apiError(detail));
      }
      load();
    }
  };

  const load = useCallback(() => {
    api.get("/jobs").then(({ data }) => setJobs(data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canWrite) return;
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/users", { params: { role: "installer" } }).then(({ data }) => setInstallers(data));
    api.get("/users", { params: { role: "central_sales,central_sales_team" } }).then(({ data }) =>
      setCsUsers(data.filter((u) => u.active !== false))
    );
    if (isCentral) api.get("/studios").then(({ data }) => setStudios(data));
  }, [canWrite, isCentral]);

  const selectedCustomer = customers.find((c) => c.id === form.customer_id);
  const needsCsPerson = /central sales/i.test(selectedCustomer?.lead_source || "");
  const csLocked = isCentralRole(user.role);

  const autoFill = async (customerId, services, sqft) => {
    const cust = customers.find((c) => c.id === customerId);
    const film = /glossy|gloss|matte|x250|x 250/i.test(services || "");
    try {
      const { data } = await api.get("/pricing/auto", {
        params: {
          make: cust?.vehicle?.make || "",
          model: cust?.vehicle?.model || "",
          service: services || "",
          ...(sqft === "" || sqft == null ? {} : { sqft: Number(sqft) }),
        },
      });
      setForm((f) => ({
        ...f,
        ppf_sqft: f.ppf_sqft === "" && data.sqft ? String(data.sqft) : f.ppf_sqft,
        price: film && data.price != null ? String(data.price) : f.price,
      }));
      setPriceInfo(film ? data : null);
    } catch {
      setPriceInfo(null);
    }
  };

  // Central Sales B2C: auto-attribute the lead to themselves and lock it.
  useEffect(() => {
    if (needsCsPerson && csLocked && form.cs_person_id !== user.id) {
      setForm((f) => ({ ...f, cs_person_id: user.id }));
    }
  }, [needsCsPerson, csLocked, user.id, form.cs_person_id]);

  const submit = async () => {
    if (needsCsPerson && !form.cs_person_id) {
      toast.error("Select which Central Sales person acquired this lead");
      return;
    }
    if (!form.customer_id) {
      toast.error("Select a customer");
      return;
    }
    try {
      await api.post("/jobs", {
        customer_id: form.customer_id,
        installer_id: form.installer_id || null,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
        status: form.status,
        price: Number(form.price || 0),
        ppf_sqft: form.ppf_sqft === "" ? null : Number(form.ppf_sqft),
        cs_person_id: needsCsPerson ? form.cs_person_id : null,
        studio_id: isCentral ? form.studio_id || null : null,
        entry_date: form.entry_date || null,
      });
      toast.success("Job created");
      setOpen(false);
      setForm({ customer_id: "", installer_id: "", services: "", status: "Lead", price: "", cs_person_id: "", studio_id: "", entry_date: "", ppf_sqft: "" });
      setInstallerSource("hq");
      setPriceInfo(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHead
        title={user.role === "installer" ? "My Jobs" : "Jobs"}
        subtitle={user.role === "network_admin" ? "All studios" : user.studio_name}
      >
        <ExportButtons
          testidPrefix="jobs"
          filename="veloskin-jobs"
          title="Job Report"
          subtitle={user.role === "network_admin" ? "All studios" : user.studio_name}
          columns={EXPORT_COLUMNS}
          rows={jobs}
        />
        {canWrite && <CreateLeadDialog onCreated={load} />}
        {canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-job-btn">
                <Plus className="mr-2 h-4 w-4" /> New job
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>New job</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Customer *</Label>
                  <Select
                    value={form.customer_id}
                    onValueChange={(v) => { setForm((f) => ({ ...f, customer_id: v, ppf_sqft: "" })); autoFill(v, form.services, ""); }}
                  >
                    <SelectTrigger data-testid="job-customer-select">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} — {c.vehicle.make} {c.vehicle.model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isCentral && (
                  <div data-testid="job-studio-block">
                    <Label>Studio (optional — leave blank to keep as an unassigned lead)</Label>
                    <Select
                      value={form.studio_id || "none"}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, studio_id: v === "none" ? "" : v }))
                      }
                    >
                      <SelectTrigger data-testid="job-studio-select">
                        <SelectValue placeholder="Assign a studio later" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Leave unassigned</SelectItem>
                        {studios.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {needsCsPerson && (
                  <div data-testid="job-cs-person-block">
                    <Label>
                      Which Central Sales person? <span className="text-rose-500">*</span>
                    </Label>
                    <p className="mb-1 text-xs text-slate-500">
                      Lead source is {selectedCustomer?.lead_source} — attribute the acquisition.
                    </p>
                    {csLocked ? (
                      <Input
                        data-testid="job-cs-person-locked"
                        className="bg-slate-50 text-slate-500"
                        value={user.name}
                        readOnly
                      />
                    ) : (
                      <Select
                        value={form.cs_person_id}
                        onValueChange={(v) => setForm((f) => ({ ...f, cs_person_id: v }))}
                      >
                        <SelectTrigger data-testid="job-cs-person-select">
                          <SelectValue placeholder="Select Central Sales person" />
                        </SelectTrigger>
                        <SelectContent>
                          {csUsers.length === 0 && (
                            <div className="px-2 py-1.5 text-sm text-slate-400">
                              No active Central Sales users
                            </div>
                          )}
                          {csUsers.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                {isCommittedStage(form.status, STATUSES) && (
                  <div data-testid="job-installer-block">
                    <Label>PPF Installer</Label>
                    <div className="mt-1.5 flex gap-2">
                      {[
                        ["hq", "HQ installers"],
                        ["studio", "Own installers"],
                      ].map(([k, label]) => (
                        <button
                          key={k}
                          type="button"
                          data-testid={`job-installer-source-${k}`}
                          onClick={() => {
                            setInstallerSource(k);
                            // "Own installers" completes the field on its own — no person needed.
                            setForm((f) => ({ ...f, installer_id: k === "studio" ? "own" : "" }));
                          }}
                          className={`min-h-[36px] rounded px-3 text-xs font-medium transition-colors duration-200 ${
                            installerSource === k
                              ? "bg-brand text-white"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {installerSource === "hq" ? (
                      <Select
                        value={form.installer_id === "own" ? "" : form.installer_id}
                        onValueChange={(v) => setForm((f) => ({ ...f, installer_id: v }))}
                      >
                        <SelectTrigger data-testid="job-installer-select" className="mt-1.5">
                          <SelectValue placeholder="Assign PPF installer" />
                        </SelectTrigger>
                        <SelectContent>
                          {installers
                            .filter((i) => (i.installer_type || "studio") === "hq")
                            .map((i) => (
                              <SelectItem key={i.id} value={i.id}>
                                {i.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-2 text-xs font-medium text-emerald-700" data-testid="job-own-installer-note">
                        Own Installer — no specific person needed.
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <Label>Services</Label>
                  <Select
                    value=""
                    onValueChange={(v) => {
                      const newServices = form.services ? `${form.services}, ${v}` : v;
                      setForm((f) => ({ ...f, services: newServices }));
                      autoFill(form.customer_id, newServices, form.ppf_sqft);
                    }}
                  >
                    <SelectTrigger data-testid="job-service-picker">
                      <SelectValue placeholder="Add a service from the catalog" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((s) => (
                        <SelectItem key={s.id} value={s.name}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    data-testid="job-services-input"
                    value={form.services}
                    onChange={(e) => setForm((f) => ({ ...f, services: e.target.value }))}
                    onBlur={(e) => autoFill(form.customer_id, e.target.value, form.ppf_sqft)}
                    placeholder="Veloskin X250, Ceramic Coating 9H"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>PPF sqft</Label>
                    <Input
                      data-testid="job-sqft-input"
                      value={form.ppf_sqft}
                      onChange={(e) => setForm((f) => ({ ...f, ppf_sqft: e.target.value }))}
                      onBlur={(e) => autoFill(form.customer_id, form.services, e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select
                      value={form.status}
                      onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
                    >
                      <SelectTrigger data-testid="job-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} className="capitalize">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Price (₹)</Label>
                    <Input
                      data-testid="job-price-input"
                      value={form.price}
                      onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                    />
                    {priceInfo && priceInfo.price != null && (
                      <p className="mt-1 text-xs text-emerald-700" data-testid="job-price-autofill-hint">
                        Auto · {priceInfo.premium ? "Premium" : "Standard"} · {priceInfo.column} · {priceInfo.tier_sqft} sqft tier
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <BackdateField
                value={form.entry_date}
                onChange={(v) => setForm((f) => ({ ...f, entry_date: v }))}
              />
              <DialogFooter>
                <Button data-testid="save-job-btn" onClick={submit} disabled={!form.customer_id || (needsCsPerson && !form.cs_person_id)}>
                  Create job
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHead>

      {canWrite && jobs.length > 0 && (
        <p className="mb-4 text-xs text-slate-500">Open a job to change its stage from the Status dropdown.</p>
      )}

      {jobs.length > 0 && (
        <div className="mb-4 max-w-md">
          <Input
            data-testid="jobs-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by customer, car model, phone or job number…"
          />
          {q && (
            <p className="mt-1 text-xs text-slate-500" data-testid="jobs-search-count">
              {filteredJobs.length} match{filteredJobs.length === 1 ? "" : "es"} for “{search.trim()}”
            </p>
          )}
        </div>
      )}

      {jobs.length === 0 && (
        <div
          className="mb-4 rounded-md border border-brand-line bg-white p-5"
          data-testid="jobs-empty-state"
        >
          <p className="font-display text-base font-semibold text-brand-ink">
            This is your pipeline — it fills up left to right
          </p>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            {canWrite
              ? "Every job moves through the pipeline from the first enquiry as a Lead all the way to Repeat/Referral. Central Sales can create a lead and assign it to a studio now or leave it unassigned. Open a job to change its stage from the Status dropdown."
              : "Every job studios create moves through these stages, from the first enquiry as a Lead all the way to Repeat/Referral. Cards will appear here as studios start working."}
          </p>
          {canWrite && (
            <Button className="mt-4" data-testid="jobs-empty-add-btn" onClick={() => setOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Create your first job
            </Button>
          )}
        </div>
      )}

      <JobsBoard
        jobs={filteredJobs}
        canEdit={canWrite}
        onMove={moveJob}
        stages={STATUSES}
        showStudio={user.is_network}
        canDelete={canDelete}
        onDelete={setDelTarget}
        canDeleteJob={(j) => canDeleteRecord(user, j, "jobs:delete")}
      />

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-job-dialog">
          <DialogHeader>
            <DialogTitle>Delete job?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This permanently deletes <strong>{delTarget?.customer_name}</strong>'s job
            {delTarget?.services?.length ? ` (${delTarget.services.join(", ")})` : ""} along with
            its interaction log and any warranty registered against it. The customer record stays.
            This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelTarget(null)}>
              Cancel
            </Button>
            <Button
              className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-delete-job-btn"
              disabled={deleting}
              onClick={doDelete}
            >
              {deleting ? "Deleting…" : "Delete job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
